import { createContext, useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import {
  getCurrentUser,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  fetchAuthSession,
  AuthUser,
} from 'aws-amplify/auth';

export interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    authorizationCode: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthState | null>(null);

// Best-effort sign-out used before a sign-in attempt. Amplify v6 throws
// UserAlreadyAuthenticatedException if signIn is called while a session is
// already cached, so we clear any existing session first. Never throws.
async function clearExistingSession(): Promise<void> {
  try {
    await amplifySignOut();
  } catch {
    // Nothing to sign out, or sign-out failed — proceed to sign in anyway.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    // A lingering session (valid tokens still in browser storage) makes Amplify
    // throw UserAlreadyAuthenticatedException. Clear it first so a valid
    // email + password always signs in as that user.
    await clearExistingSession();
    await amplifySignIn({ username: email, password });
    const u = await getCurrentUser();
    setUser(u);
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, displayName: string, authorizationCode: string) => {
    await amplifySignUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          'custom:display_name': displayName,
        },
        // Passed to the PRE_SIGN_UP trigger as request.clientMetadata, where the
        // authorization code is validated. Sign-up is rejected server-side if
        // the code isn't valid, so this gate can't be bypassed from the client.
        clientMetadata: { authorizationCode },
      },
    });
    // Auto-confirm pre-sign-up trigger means the user is already CONFIRMED;
    // sign them in immediately. Clear any lingering session first (same reason
    // as signIn) so the new account is the one that ends up authenticated.
    await clearExistingSession();
    await amplifySignIn({ username: email, password });
    const u = await getCurrentUser();
    setUser(u);
  }, []);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({ user, isLoading, signIn, signUp, signOut, getIdToken }),
    [user, isLoading, signIn, signUp, signOut, getIdToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
